import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { DefaultTFuncReturn } from "i18next";

export type PageHeadingOverride = {
  /** Accepts plain strings or `t()` return values from react-i18next. */
  title?: DefaultTFuncReturn;
  description?: DefaultTFuncReturn;
  hidden?: boolean;
};

type PageHeadingContextValue = {
  override: PageHeadingOverride | null;
  setOverride: (value: PageHeadingOverride | null) => void;
};

const PageHeadingContext = createContext<PageHeadingContextValue | null>(null);

export function PageHeadingProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<PageHeadingOverride | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return <PageHeadingContext.Provider value={value}>{children}</PageHeadingContext.Provider>;
}

export function usePageHeadingContext(): PageHeadingContextValue {
  const ctx = useContext(PageHeadingContext);
  if (!ctx) {
    throw new Error("usePageHeadingContext must be used within PageHeadingProvider");
  }
  return ctx;
}

/**
 * Override global title/description for the current route (e.g. dynamic stats or loading state).
 * Clears on unmount.
 */
export function usePageHeadingOverride(override: PageHeadingOverride | null) {
  const { setOverride } = usePageHeadingContext();
  useEffect(() => {
    setOverride(override);
    return () => setOverride(null);
  }, [setOverride, override?.title, override?.description, override?.hidden]);
}
